import { useGetChannelSchemaLazyQuery, usePingPreviewMutation } from '@generated/graphql';
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { GraphDataContext } from './GraphDataProvider';
import { ChannelNode } from './types';

type ChannelDetailContextType = {
  channelNodes: ChannelNode[];
};

export const ChannelDetailContext = createContext<ChannelDetailContextType>({
  channelNodes: [],
});

export const ChannelDetailProvider = ({ children }: PropsWithChildren) => {
  const [channelNodes, setChannelNodes] = useState<ChannelNode[]>([]);
  const { graphMetadata } = useContext(GraphDataContext);
  const [pingPreview] = usePingPreviewMutation();
  const [getChannelSchema] = useGetChannelSchemaLazyQuery();

  useEffect(() => {
    if (graphMetadata?.channelId) {
      getChannelSchema({
        variables: { channelId: graphMetadata?.channelId },
      })
        .then(({ data }) => {
          if (data?.getChannelSchema) {
            const schema = JSON.parse(data?.getChannelSchema || '') as ChannelNode[];
            setChannelNodes(schema);
          }
        })
        .catch(() => console.error('Error retrieving channel schema.'));

      void pingPreview({ variables: { channelId: graphMetadata.channelId } });
      const intervalId = setInterval(() => {
        void pingPreview({ variables: { channelId: graphMetadata.channelId } });
      }, 60000);
      return () => clearInterval(intervalId);
    }
    return () => {};
  }, [graphMetadata?.channelId, getChannelSchema, pingPreview]);

  const valueProvider = useMemo(() => ({ channelNodes }), [channelNodes]);
  return (
    <ChannelDetailContext.Provider value={valueProvider}>{children}</ChannelDetailContext.Provider>
  );
};
